import { useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';

import { Close } from '@mui/icons-material';
import {
  Snackbar,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';

import { BackrsForm } from './BackrsForm';
import { BackrsFormField, BackrsFormValues } from './types';
import { sendPasswordReset } from '../Login/login.util';

const LOGIN_STRINGS = appStrings.modules.login;

const defaultForgotPasswordValues: BackrsFormValues = {
  forgotPasswordEmail: '',
};

type ForgotPasswordPopupProps = Readonly<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  environmentUrl: string;
  source: string;
}>;

export const ForgotPasswordPopup = ({
  isOpen,
  setIsOpen,
  environmentUrl,
  source,
}: ForgotPasswordPopupProps) => {
  const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>(undefined);

  const closeModal = () => setIsOpen(false);

  const fields: BackrsFormField[] = [
    {
      id: 'forgotPasswordEmail',
      name: 'forgotPasswordEmail',
      placeholder: LOGIN_STRINGS.emailPlaceholder,
      type: 'text',
      autoComplete: 'email',
      autoFocus: true,
      rules: {
        required: LOGIN_STRINGS.error.emailRequired,
        pattern: { value: /^\S+@\S+$/i, message: LOGIN_STRINGS.error.invalidEmail },
      },
    },
  ];

  const handleSubmission = async (data: BackrsFormValues) => {
    try {
      await sendPasswordReset(data.forgotPasswordEmail, environmentUrl, source);
      setSnackbarMessage(LOGIN_STRINGS.forgotPasswordSuccess);
    } catch {
      console.log('Error sending password reset email');
    } finally {
      closeModal();
    }
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(undefined)}
        message={snackbarMessage}
      />
      <Dialog
        PaperProps={{
          style: { borderRadius: 8 },
        }}
        open={isOpen}
        onClose={closeModal}
      >
        <DialogTitle mt={2}>
          <IconButton
            id="forgot-password-close-button"
            aria-label="close"
            onClick={closeModal}
            sx={dialogCloseButtonStyle}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>{LOGIN_STRINGS.forgotPasswordPrompt}</Typography>
          <BackrsForm
            fields={fields}
            defaultValues={defaultForgotPasswordValues}
            buttonLabel={LOGIN_STRINGS.forgotPassowordButton}
            buttonId="forgot-password-submit-button"
            onSubmit={handleSubmission}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const dialogCloseButtonStyle: SxProps<Theme> = {
  position: 'absolute',
  right: 8,
  top: 8,
};
